import { Workbook } from "exceljs";
import dayjs from "dayjs";
import {
	VisitAnalyteResponse,
	VisitDetailFilters,
	VisitDetailRow,
	reportsService,
} from "../../services/reports.service";
import { addCoverSheet } from "./cover-sheet";
import { applyHeaderStyle, currencyFmt, filenameSlug } from "./workbook-styles";
import { BuiltWorkbook } from "./build-workbook";
import { getContrastText } from "../colorUtils";

export interface VisitDetailWorkbookFilters extends VisitDetailFilters {
	programName?: string;
	/**
	 * SIMS-8104 (AC3) — the names of every SELECTED program, alphabetical.
	 *
	 * Drives two things the AC requires: the cover sheet's program list, and the
	 * filename. Optional, so a caller that has not been updated still produces the
	 * single-program output it produced before (`programName`), rather than an
	 * export that silently loses its program label.
	 */
	programNames?: string[];
	// Currency symbol from the account's `currency` setting (e.g. "$"), applied
	// to the Incentive Earned column so the .xlsx matches the on-screen / PDF.
	currencyMask?: string;
	// Human-readable filter values for the cover sheet (optional).
	orgUnitFilter?: string;
	researcherFilter?: string;
	/**
	 * SIMS-8903 — every applied filter as [label, value], from the SAME source the
	 * PDF header uses, so the two exports can no longer disagree about what was
	 * filtered. Passing ids here (as `providerId`/`orgUnitIds` above are) is what
	 * left the cover printing "All": the sheet needs display names, which only the
	 * tab can resolve.
	 */
	filterPairs?: Array<[string, string]>;
	// SIMS-7770: account country date format (US MM/DD/YYYY / UK DD/MM/YYYY).
	dateFormat?: string;
}

/**
 * SIMS-7588 — Build the Visit Detail Excel export ENTIRELY IN THE BROWSER.
 *
 * The backend `/reports/visit-detail/export` endpoint returned the workbook as a
 * binary body that had to cross API Gateway + the edge-optimized CloudFront
 * custom domain. That path base64-encodes the body and the base64 arrived at
 * the browser UNDECODED, so Excel rejected the file. Building the workbook
 * client-side (from JSON) avoids any binary crossing API Gateway.
 *
 * Structure mirrors the PDF (VisitDetailTab.handlePdf), which the report is
 * expected to match: two sheets —
 *   - "Visits":   one row per visit (visit-level fields).
 *   - "Analytes": one row per visit-analyte, only for visits that captured cup
 *                 data (event.cupsAndAnalytes + cupId). Empty when none.
 */
export const buildVisitDetailWorkbook = async (
	filters: VisitDetailWorkbookFilters,
): Promise<BuiltWorkbook> => {
	const rows = await reportsService.fetchAllVisitDetail({
		accountId: filters.accountId,
		programId: filters.programId,
		orgUnitIds: filters.orgUnitIds,
		payerIds: filters.payerIds,
		groupLabelIds: filters.groupLabelIds,
		providerId: filters.providerId,
		startDate: filters.startDate,
		endDate: filters.endDate,
		cin: filters.cin,
	});

	// SIMS-8104 (AC3): the selected programs, alphabetical. Falls back to the
	// single `programName` so an un-updated caller keeps its previous behaviour.
	const programNames = (
		filters.programNames?.length
			? [...filters.programNames]
			: [filters.programName ?? "Program"]
	).sort((a, b) => a.localeCompare(b));
	const isMultiProgram = programNames.length > 1;

	// The cover sheet's "Program" value: every selected program, comma-separated,
	// so the exported file states its own scope rather than leaving the reader to
	// infer it from the rows.
	const programName = programNames.join(", ");
	const earnedFmt = currencyFmt(filters.currencyMask ?? "$");
	const dateFmt = filters.dateFormat ?? "MM/DD/YYYY";

	const workbook = new Workbook();
	workbook.creator = "SIMS Reporting";
	workbook.created = new Date();

	addCoverSheet(workbook, {
		programName,
		exportedAt: new Date(),
		startDate: filters.startDate
			? dayjs(filters.startDate).format(dateFmt)
			: undefined,
		endDate: filters.endDate
			? dayjs(filters.endDate).format(dateFmt)
			: undefined,
		orgUnitFilter: filters.orgUnitFilter,
		researcherFilter: filters.researcherFilter,
		// SIMS-8903: when present these REPLACE the legacy rows above, so the cover
		// states Payer, Group labels, Provider and CIN instead of omitting them and
		// printing "Researcher filter: All" over a real provider selection.
		filters: filters.filterPairs,
	});

	// --- Sheet 1: Visits (one row per visit) — mirrors PDF "Visits" section. ---
	const visits = workbook.addWorksheet("Visits");
	// SIMS-8104 AC1: Program is APPENDED to the existing column set, matching the
	// on-screen column ruling (kickoff Q7/Q8) — no existing column is renamed or
	// reordered, so a consumer reading this sheet by column position is unaffected.
	//
	// SIMS-8916 removed the Enrollment Status column that SIMS-8104 added here. It
	// was the LAST column, so this is the one removal that does not shift any other
	// column's position either.
	visits.columns = [
		{ header: "Visit Date", key: "visitDate", width: 14 },
		{ header: "Visit Number", key: "visitNumber", width: 14 },
		{ header: "Member Name", key: "member", width: 24 },
		{ header: "CIN", key: "cin", width: 14 },
		{ header: "Org Unit", key: "orgUnit", width: 18 },
		{ header: "Provider", key: "provider", width: 20 },
		{ header: "Event Name", key: "eventName", width: 18 },
		{ header: "Visit Result", key: "result", width: 16 },
		{ header: "Incentive Earned", key: "earned", width: 16 },
		{ header: "Payer Plan", key: "payerPlanName", width: 18 },
		{ header: "Payer Plan ID", key: "payerPlanNumber", width: 14 },
		{ header: "Program", key: "programName", width: 24 },
	];
	applyHeaderStyle(visits.getRow(1));
	for (const row of rows) {
		const r = visits.addRow({
			visitDate: row.visitDate ? dayjs(row.visitDate).format(dateFmt) : "",
			visitNumber: row.visitNumber ?? "",
			// SIMS-8900: an em dash, matching the on-screen table. A blank cell is
			// indistinguishable from a failed export, and the name is legitimately
			// absent whenever the account masks it (`displayBeneficiaryName`).
			member: row.member ?? "—",
			cin: row.cin ?? "",
			orgUnit: row.orgUnit ?? "",
			provider: row.provider ?? "",
			eventName: row.eventName ?? "",
			result: row.result ?? "",
			earned: row.earned,
			payerPlanName: row.payerPlanName ?? "",
			payerPlanNumber: row.payerPlanNumber ?? "",
			// SIMS-8104 AC1/AC2. Full program name, matching the on-screen column
			// (the design's abbreviations are not a documented naming scheme).
			programName: row.programName ?? "",
		});
		r.getCell("earned").numFmt = earnedFmt;
		// SIMS-7984: tint the result cell with the result's configured colour, the
		// same source the on-screen chip and the PDF use. Rows with no matched
		// result ("Invalid", "Missed") carry no colour and stay unfilled.
		if (row.resultColor) {
			const cell = r.getCell("result");
			cell.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: {
					argb: `FF${row.resultColor.replace("#", "").toUpperCase()}`,
				},
			};
			cell.font = {
				color: {
					argb: `FF${getContrastText(row.resultColor).replace("#", "")}`,
				},
				bold: true,
			};
		}
	}

	// --- Sheet 2: Analytes (one row per visit-analyte) — mirrors PDF "Analytes". ---
	const analyteSheet = workbook.addWorksheet("Analytes");
	// SIMS-8104 AC1: this sheet gains Program too — the Hi-Fi states it explicitly
	// ("The analyte export sheet carries the Program column too.").
	// Enrollment Status never appeared on this sheet (SIMS-8104 AC2) and SIMS-8916
	// has now removed it from the Visits sheet too, so neither sheet carries it.
	analyteSheet.columns = [
		{ header: "Visit Date", key: "visitDate", width: 14 },
		{ header: "Visit Number", key: "visitNumber", width: 14 },
		{ header: "Member Name", key: "member", width: 24 },
		{ header: "CIN", key: "cin", width: 14 },
		{ header: "Event Name", key: "eventName", width: 18 },
		{ header: "Cup Name", key: "cupName", width: 18 },
		// Dropped by 3ff10419 (SIMS-7588) along with the scientific name. TWO columns,
		// as they were: a Yes/No that Excel can filter and pivot on, and the reasons
		// as their own text. The on-screen card merges them ("Yes — reason, reason")
		// because it is prose; an export is not.
		{ header: "Tampered", key: "isTampered", width: 12 },
		// SIMS-7588 collapsed the analyte identity into a single "Analyte Name"
		// carrying only the short name, dropping "Scientific Name" outright. Both
		// come back, short name first and scientific name to its right — the same
		// order the on-screen analyte panel uses, so the export reads like the screen.
		{ header: "Short Name", key: "analyteShortName", width: 16 },
		{ header: "Scientific Name", key: "analyteScientificName", width: 24 },
		{ header: "Analyte Result", key: "analyteResult", width: 16 },
		{ header: "Tampered Reasons", key: "tamperedReasons", width: 28 },
		{ header: "Payer Plan", key: "payerPlanName", width: 18 },
		{ header: "Payer Plan ID", key: "payerPlanNumber", width: 14 },
		{ header: "Program", key: "programName", width: 24 },
	];
	applyHeaderStyle(analyteSheet.getRow(1));

	// Resolve analyte drill-downs once per visit (cache by enrollment:udtResult).
	const analytesCache = new Map<string, VisitAnalyteResponse | null>();
	const analytesFor = async (
		row: VisitDetailRow,
	): Promise<VisitAnalyteResponse | null> => {
		if (!row.hasAnalyteDetails) return null;
		const key = `${row.enrollmentId}:${row.udtResultId}`;
		const cached = analytesCache.get(key);
		if (cached !== undefined) return cached;
		try {
			const data = await reportsService.getVisitAnalytes(
				filters.accountId,
				row.enrollmentId,
				row.udtResultId,
			);
			analytesCache.set(key, data);
			return data;
		} catch {
			analytesCache.set(key, null);
			return null;
		}
	};

	for (const row of rows) {
		const analyteData = await analytesFor(row);
		if (!analyteData || analyteData.analytes.length === 0) continue;
		for (const a of analyteData.analytes) {
			analyteSheet.addRow({
				visitDate: row.visitDate ? dayjs(row.visitDate).format(dateFmt) : "",
				visitNumber: row.visitNumber ?? "",
				// SIMS-8900: an em dash, matching the on-screen table. A blank cell is
				// indistinguishable from a failed export, and the name is legitimately
				// absent whenever the account masks it (`displayBeneficiaryName`).
				member: row.member ?? "—",
				cin: row.cin ?? "",
				eventName: row.eventName ?? "",
				cupName: analyteData.cupName ?? "",
				isTampered: analyteData.isTampered ? "Yes" : "No",
				tamperedReasons: (analyteData.tamperedReasons ?? []).join(", "),
				analyteShortName: a.shortName ?? "—",
				analyteScientificName: a.scientificName ?? "—",
				analyteResult: a.positive ? "Positive" : "Negative",
				payerPlanName: row.payerPlanName ?? "",
				payerPlanNumber: row.payerPlanNumber ?? "",
				// SIMS-8104 AC1 — Program on the analyte sheet too.
				programName: row.programName ?? "",
			});
		}
	}

	const arrayBuffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([arrayBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});

	// SIMS-8104 (AC3): the filename reflects a multi-program export.
	//
	// PROPOSED PATTERN, approved at Gate 1 and flagged to the PO for confirmation
	// (comment 65279): a single program keeps today's `visit_detail_<program>.xlsx`
	// exactly, and several programs become `visit_detail_<N>-programs.xlsx`.
	//
	// FR-17 says nothing about filenames and no example exists anywhere, so this is
	// a decision rather than a reading. Concatenating names was rejected: with the
	// design's own three programs it produces a ~60-character filename, and program
	// names can contain punctuation and non-ASCII that `filenameSlug` would strip
	// into something unreadable or even colliding.
	const filename = `visit_detail_${
		isMultiProgram
			? `${programNames.length}-programs`
			: filenameSlug(programNames[0])
	}.xlsx`;
	return { blob, filename };
};
